import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonObject } from '../../app/json/jsonValue.js';
import { CommonUtil } from '../../common/commonUtil.js';
import { ILoaderNormal } from './iLoaderNormal.js';
import type { GetTemplateResult } from './iLoaderNormal.js';
import { AppSiteTemplateData } from '../../model/appSiteTemplateData.js';
import { InheritanceOperation, NavigationDecision, ResourceStatistics, TemplateEntry, TemplateStore } from '../../model/index.js';
import { DynamicResults } from '../../model/dynamicResults.js';
import type { IConfig, AppFileResult } from '../../config/iConfig.js';
import type { PreparedContent } from '../../config/preparedContent.js';
import { DynamicConfigMap } from '../../registry/dynamicConfigMap.js';
import { TransientStore } from '../../registry/transientStore.js';
import type { ITransientStore } from '../../registry/iface/iTransientStore.js';
import type { AppSiteResult } from '../../registry/iface/appSiteResult.js';
import { apiMethods, apiStaticMethods } from './loaderNormal_Api.js';
import { inheritanceMethods } from './loaderNormal_Inheritance.js';
import { dynamicMethods } from './loaderNormal_Dynamic.js';
import { accessorMethods, accessorStaticMethods } from './loaderNormal_Accessors.js';
import { configPropertyDescriptors, iloaderMethods } from './loaderNormal_ILoader.js';
import { lifecycleMethods } from './loaderNormal_Lifecycle.js';
import type { ILoaderTestSupport } from '../../base/loader/iLoaderTestSupport.js';
import type { PhaseTiming } from '../../base/timing/phaseTiming.js';
import { TimingPhase } from '../../base/timing/timingPhase.js';

type TemplateLookupResult = {
    html: string;
    json: JsonObject | null;
    templateKey: string;
    resolvedAppSite: string;
    found: boolean;
};

type TemplateLookupContext = {
    requestAppSite: string;
    appSiteTemplates: Map<string, TemplateEntry>;
};

type ReuseDataJsonResult = {
    json: JsonObject | null;
    returnName: string;
};

/**
 * Loader implementation for EngineNormal
 * Handles loading and caching of HTML and JSON templates from the file system
 * Templates are kept immutable within the loader
 */
export class LoaderNormal extends ILoaderNormal implements ILoaderTestSupport {
    // #region Private Variables

    // Instance-level store - separates loaded data from per-merge mutable state
    _store: TemplateStore = new TemplateStore();

    _appSite: string = '';
    _config: IConfig | null = null;

    // Track Inheritance operations for flow data
    _inheritanceOperations: InheritanceOperation[] = [];

    // Per-merge mutable state for dynamic handler results
    // Engine creates and owns the DynamicResults; loader writes to it via setDynamicResults
    _dynamicResults: DynamicResults = new DynamicResults();
    _transientStore: ITransientStore = new TransientStore();

    // ActionMode flag - when true, DynamicAction handlers are executed
    _actionMode: boolean = false;

    // Skip patterns for DynamicView/DynamicData/DynamicAction handlers
    _skipDynamicView: string | null = null;
    _skipDynamicData: string | null = null;

    // Resource statistics are stored in _store

    // Pre-loaded stores (populated during Load, used during engine processing)
    _dynamicDataConfigStore: Record<string, DynamicConfigMap | null> = {};    // appSite -> DynamicConfigMap
    _dynamicClientDataConfigStore: Record<string, DynamicConfigMap | null> = {}; // appSite -> DynamicConfigMap
    _dynamicViewConfigStore: Record<string, DynamicConfigMap | null> = {};    // appSite -> DynamicConfigMap
    _dynamicActionConfigStore: Record<string, DynamicConfigMap | null> = {};  // appSite -> DynamicConfigMap
    _dynamicAppSiteConfigStore: Record<string, DynamicConfigMap | null> = {}; // appSite -> DynamicConfigMap
    _dynamicDataParamsStore: JsonObject = new JsonObject();    // "appSite:paramFile" -> JsonValue.object
    _dynamicClientDataParamsStore: JsonObject = new JsonObject(); // "appSite:paramFile" -> JsonValue.object
    _dynamicViewParamsStore: JsonObject = new JsonObject();    // "appSite:paramFile" -> JsonValue.object
    _dynamicActionParamsStore: JsonObject = new JsonObject();  // "appSite:paramFile" -> JsonValue.object
    _dynamicAppSiteParamsStore: JsonObject = new JsonObject(); // "appSite:paramFile" -> JsonValue.object

    // Per-merge request context (stashed at merge top from applyDynamicAppSiteHandlers). Mirrors the
    // C# LoaderNormal._requestContextJson. Reserved for the AppSite/authorization phase (later phase).
    _requestContextJson: JsonObject | null = null;
    // Per-merge navigation trace (accumulated by applyDynamicAppSiteHandlers; drained by the engine into
    // MergeResult.navigationTrace via getAndClearNavigationTrace). Mirrors _inheritanceOperations ownership.
    _lastNavigationTrace: NavigationDecision[] = [];
    _globalDataStore: JsonObject = new JsonObject();           // appSite -> JsonValue.object|null
    _globalViewDataStore: JsonObject = new JsonObject();       // appSite -> JsonValue.object|null
    _globalViewDataComponentStore: JsonObject = new JsonObject(); // "appSite:componentName" -> JsonValue.object

    // Client-mode fields (set externally for client-side rendering)
    _globalData: Map<string, JsonObject> | null = null;
    _globalViewData: Map<string, JsonObject> | null = null;
    _dynamicDataParams: JsonObject | null = null;
    _dynamicViewParams: JsonObject | null = null;
    _dynamicActionParams: JsonObject | null = null;
    _dynamicAppSiteParams: JsonObject | null = null;

    // #endregion

    // #region Mixin method declarations (attached via Object.assign to prototype)

    // --- From loaderNormal_Accessors.ts (accessorMethods) ---
    declare load: (this: LoaderNormal, config: IConfig, appSite: string, timing: PhaseTiming) => boolean;
    declare _preloadDynamicAndGlobalData: (this: LoaderNormal, config: IConfig, prepared: PreparedContent, appSite: string) => void;
    declare getBaseComponentName: (this: LoaderNormal, placeholderOrSlotholderName: string, currentTemplateName: string) => { baseComponentName: string; isReuseComponent: boolean };
    declare getDynamicDataConfig: (this: LoaderNormal, appSite: string) => DynamicConfigMap | null;
    declare setDynamicResults: (this: LoaderNormal, dynamicResults: DynamicResults) => void;
    declare getDynamicActionConfig: (this: LoaderNormal, appSite: string) => DynamicConfigMap | null;
    declare getDynamicDataParams: (this: LoaderNormal, appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicClientDataConfig: (this: LoaderNormal, appSite: string) => DynamicConfigMap | null;
    declare getDynamicClientDataParams: (this: LoaderNormal, appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicViewConfig: (this: LoaderNormal, appSite: string) => DynamicConfigMap | null;
    declare getDynamicViewParams: (this: LoaderNormal, appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicActionParams: (this: LoaderNormal, appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicAppSiteConfig: (this: LoaderNormal, appSite: string) => DynamicConfigMap | null;
    declare getDynamicAppSiteParams: (this: LoaderNormal, appSite: string, paramFile: string) => JsonObject | null;
    declare ensureAppSiteLoaded: (this: LoaderNormal, appSite: string, timing: PhaseTiming) => boolean;
    declare getGlobalData: (this: LoaderNormal, appSite: string) => JsonObject | null;
    declare getGlobalViewData: (this: LoaderNormal, appSite: string) => JsonObject | null;
    declare getGlobalViewDataForComponent: (this: LoaderNormal, appSite: string, componentName: string) => JsonObject | null;
    declare setActionMode: (this: LoaderNormal, actionMode: boolean) => void;
    declare getAndClearInheritanceOperations: (this: LoaderNormal) => InheritanceOperation[];
    declare getAndClearNavigationTrace: (this: LoaderNormal) => NavigationDecision[];
    declare getAppFile: (this: LoaderNormal, appSite: string, appView: string) => AppFileResult;
    declare getTemplatesApiResponse: (this: LoaderNormal, appSite: string, appView: string, contextJson: JsonObject, serverTimeMs: number, includeSearchAppSites?: boolean, maskHtml?: boolean, orderedTemplateNames?: string[] | null) => string;
    declare setTransientStore: (this: LoaderNormal, transientStore: ITransientStore) => void;
    declare getTransientStore: (this: LoaderNormal) => ITransientStore;
    declare getDynamicParamByKey: (this: LoaderNormal, dynamicParams: JsonObject | null, storeKey: string, paramsStore: JsonObject) => JsonObject | null;
    declare resolveDynamicParamSource: (this: LoaderNormal, clientParams: JsonObject | null, appSite: string, paramFile: string) => { dynamicParams: JsonObject | null; storeKey: string };
    declare _serializePerSiteStores: (this: LoaderNormal, sb: string[], appSite: string) => void;
    declare _serializeDynamicConfigToJson: (this: LoaderNormal, config: DynamicConfigMap) => string;
    declare _serializePerSiteDynamicConfig: (this: LoaderNormal, sb: string[], allSites: string[], key: string, getter: (site: string) => DynamicConfigMap | null) => void;
    declare _serializePerSiteParamsFromStore: (this: LoaderNormal, sb: string[], allSites: string[], key: string, paramsStore: JsonObject) => void;
    declare getComponentId: (this: LoaderNormal, appSite: string, componentName: string) => string | null;
    declare getSearchAppSitesForAppSite: (this: LoaderNormal, appSite: string) => string;
    declare getResourceStatistics: (this: LoaderNormal, appSite: string) => ResourceStatistics;
    declare _computeResourceStatistics: (this: LoaderNormal, appSite: string) => ResourceStatistics;
    declare getLoaderStateJson: (this: LoaderNormal, appSite: string) => string;
    declare getLoaderPerSiteStoresJson: (this: LoaderNormal, appSite: string) => string;

    // --- From loaderNormal_Api.ts (apiMethods) ---
    declare _getAllTemplatesJson: (this: LoaderNormal, includeSearchAppSites?: boolean, orderedTemplateNames?: string[] | null) => Record<string, AppSiteTemplateData>;
    declare _buildTemplatesJsonForAppSite: (this: LoaderNormal, appSite: string, orderedTemplateNames?: string[] | null) => string;

    // --- From loaderNormal_Inheritance.ts (inheritanceMethods) ---
    declare _hasDynamicDataHandlers: (this: LoaderNormal, appSite: string, componentName: string) => boolean;
    declare _hasDynamicActionHandlers: (this: LoaderNormal, appSite: string, componentName: string) => boolean;
    declare _hasGlobalData: (this: LoaderNormal, appSite: string, componentName: string, resolvedAppSite: string | null) => boolean;
    declare getTemplateDataWithInheritance: (this: LoaderNormal, appSite: string, appView: string, appFile: string, jsonObj: JsonObject, resolvedTemplateName: string, timing: PhaseTiming, flowStack?: string[] | null, resolvedAppSite?: string | null, configJson?: JsonObject | null, componentHtml?: string | null, clientMode?: boolean) => JsonObject | null;
    declare _resolveJsonKeyWithInheritance: (this: LoaderNormal, appSite: string, actualKey: string, defaultValue: string, currentTemplateKey: string, flowStack?: string[] | null) => { resolvedValue: string | null; sourceTemplate: string | null; wasInherited: boolean };
    declare searchContextStackForKey: (this: LoaderNormal, appSite: string, key: string, flowStack: string[]) => { value: string | null; sourceTemplate: string | null };
    declare _resolveJsonArrayKeyWithInheritance: (this: LoaderNormal, appSite: string, actualKey: string, defaultArray: import('../../app/json/index.js').JsonArray, currentTemplateKey: string, flowStack?: string[] | null) => { resolvedArray: import('../../app/json/index.js').JsonArray; sourceTemplate: string | null; wasInherited: boolean };
    declare searchContextStackForArrayKey: (this: LoaderNormal, appSite: string, key: string, flowStack: string[]) => { value: import('../../app/json/index.js').JsonArray | null; sourceTemplate: string | null };
    declare _buildParentContext: (this: LoaderNormal, appSite: string, flowStack: string[]) => Map<string, string> | null;
    declare _applyGlobalData: (this: LoaderNormal, appSite: string, componentName: string, componentJson: JsonObject, resolvedAppSite: string | null) => void;
    declare _getWithFallback: (this: LoaderNormal, targetAppSite: string, appSite: string, getter: (site: string) => JsonObject | null) => JsonObject | null;
    declare _getGlobalDataWithFallback: (this: LoaderNormal, targetAppSite: string, appSite: string) => JsonObject | null;
    declare _getGlobalViewDataForComponentWithFallback: (this: LoaderNormal, targetAppSite: string, appSite: string, componentName: string) => JsonObject | null;
    declare _mergeGlobalDataIntoComponent: (this: LoaderNormal, componentJson: JsonObject, globalData: JsonObject | null) => void;
    declare _getScalarString: (this: LoaderNormal, val: import('../../app/json/jsonValue.js').JsonValue) => string | null;
    declare _matchesByStringKeys: (this: LoaderNormal, componentItem: JsonObject, globalItem: JsonObject, primaryKey: string | null) => boolean;
    declare _findKeyIgnoreCase: (this: LoaderNormal, data: JsonObject, key: string) => string | null;

    // --- From loaderNormal_Dynamic.ts (dynamicMethods) ---
    declare executeWithReadOnly: <T>(this: LoaderNormal, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, action: () => T) => T;
    declare shouldSkipDynamicViewHandlerInvocation: (this: LoaderNormal, componentHtml: string, componentName: string, resolvedComponentName: string) => boolean;
    declare resolveDynamicViewConfigSource: (this: LoaderNormal, appSite: string) => { dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap | null; dynamicConfigAppSite: string };
    declare getDynamicViewKeysToProcess: (this: LoaderNormal, appSite: string, componentName: string, dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap, dynamicConfigAppSite: string) => { keysToProcess: string[]; dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap; dynamicConfigAppSite: string };
    declare shouldSkipViewHandler: (this: LoaderNormal, iViewType: string) => boolean;
    declare tryResolveDynamicViewProvider: (this: LoaderNormal, iViewType: string, dynamicConfigAppSite: string, paramFile: string) => { viewProvider: import('../../registry/iface/iView.js').IView | null; paramJson: JsonObject };
    declare executeDynamicViewHandler: (this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentHtml: string, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, viewProvider: import('../../registry/iface/iView.js').IView, timing: PhaseTiming) => string;
    declare recordDynamicViewResult: (this: LoaderNormal, componentName: string, iViewType: string, resultHtml: string) => void;
    declare resolveDynamicDataConfigSource: (this: LoaderNormal, appSite: string) => { dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap | null; dynamicConfigAppSite: string };
    declare resolveDynamicClientDataConfigSource: (this: LoaderNormal, appSite: string) => { dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap | null; dynamicConfigAppSite: string };
    declare applyDynamicClientDataHandlers: (this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject, configJson: JsonObject | null, timing: PhaseTiming, flowStack?: string[] | null) => void;
    declare preserveDeliveredClientData: (this: LoaderNormal, componentName: string) => void;
    declare applyClientHandlers: (this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentHtml: string | null, resolvedJson: JsonObject, configJson: JsonObject | null, timing: PhaseTiming, flowStack?: string[] | null) => void;
    declare tryResolvePureClientHandlerParams: (this: LoaderNormal, appSite: string, componentName: string, clientHandlerName: string) => JsonObject | null;
    declare _hasDynamicClientDataHandlers: (this: LoaderNormal, appSite: string, componentName: string) => boolean;
    declare getDynamicDataMatchKey: (this: LoaderNormal, componentName: string, dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap) => string | null;
    declare shouldSkipDataHandler: (this: LoaderNormal, iDataType: string) => boolean;
    declare tryResolveDynamicDataProvider: (this: LoaderNormal, iDataType: string, dynamicConfigAppSite: string, paramFile: string) => { dataProvider: import('../../registry/iface/iData.js').IData | null; paramJson: JsonObject };
    declare executeDynamicDataHandler: (this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, dataHandler: import('../../registry/iface/iData.js').IData, getParentJsonValue: ((key: string) => string | null) | null, timing: PhaseTiming) => JsonObject;
    declare mergeDynamicDataResult: (this: LoaderNormal, componentJson: JsonObject, resultJson: JsonObject | null) => void;
    declare replaceJsonObjectContents: (this: LoaderNormal, targetJson: JsonObject, sourceJson: JsonObject) => void;
    declare recordDynamicDataResult: (this: LoaderNormal, componentName: string, iDataType: string, componentJson: JsonObject) => void;
    declare syncDynamicActionResultsAfterDataMutation: (this: LoaderNormal, componentName: string, afterJson: unknown) => void;
    declare resolveDynamicActionConfigSource: (this: LoaderNormal, appSite: string) => { dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap | null; dynamicConfigAppSite: string };
    declare tryResolveDynamicActionProvider: (this: LoaderNormal, iActionType: string, dynamicConfigAppSite: string, paramFile: string) => { registered: boolean; paramJson: JsonObject };
    declare tryBuildParentJsonValueHandlerForAction: (this: LoaderNormal, appSite: string, flowStack: string[] | null, contextJson?: JsonObject | null) => ((key: string) => string | null) | null;
    declare executeDynamicAction: (this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, actionJson: JsonObject | null, actionTypeName: string, getParentJsonValue: ((key: string) => string | null) | null, timing: PhaseTiming) => void;
    declare executeDynamicActionHandler: (this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, actionJson: JsonObject | null, actionTypeName: string, getParentJsonValue: ((key: string) => string | null) | null, timing: PhaseTiming) => void;
    declare recordDynamicActionResult: (this: LoaderNormal, componentName: string, iActionType: string, componentJson: JsonObject) => void;
    declare buildAppSiteSearchOrder: (this: LoaderNormal, appSite: string) => string[];
    declare applyDynamicViewHandlers: (this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtml: string, componentJson: JsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson: JsonObject | null, timing: PhaseTiming) => string;
    declare _applyDynamicViewHandlers: (this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtml: string, componentJson: JsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson: JsonObject | null, timing: PhaseTiming) => string;
    declare applyDynamicDataHandlers: (this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject, timing: PhaseTiming, configJson?: JsonObject | null, flowStack?: string[] | null) => void;
    declare applyDynamicActionHandlers: (this: LoaderNormal, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentJson: JsonObject, resolvedAppSite: string, resolvedComponentName: string, timing: PhaseTiming, configJson?: JsonObject | null, contextJson?: JsonObject | null, flowStack?: string[] | null) => void;
    declare applyDynamicAppSiteHandlers: (this: LoaderNormal, appSite: string, appView: string | null, contextJson: JsonObject, configJson: JsonObject | null, timing: PhaseTiming) => AppSiteResult;

    // --- Static method declarations ---
    static loadTemplatesFromSingleAppSite: (config: IConfig, prepared: PreparedContent, appSite: string, timing: PhaseTiming) => Map<string, TemplateEntry>;
    static BuildAppSitesToLoad: (appSite: string, searchAppSites: string) => string[];
    static _shouldSkipHandler: (handlerType: string, skipPatterns: string | null) => boolean;
    static _countJsonKeysAndArrays: (json: JsonObject, stats: ResourceStatistics) => void;
    static _countHandlersInConfig: (config: JsonObject | DynamicConfigMap) => number;
    static _escapeJsonString: (value: string) => string;

    // #endregion

    // #region Constructor

    /**
     * Creates a new LoaderNormal instance and automatically loads templates
     * @param config - Configuration interface providing file path metadata and SearchAppSites (required)
     * @param appSite - Primary AppSite name to load
     * @param timing - Records the load phases (source, dynamic)
     */
    constructor(config: IConfig, appSite: string, timing: PhaseTiming) {
        super();
        timing.beginMeasure(TimingPhase.Prepare);
        try {
            this._appSite = '';
            this._config = config;
            this._inheritanceOperations = [];
            this._dynamicResults = new DynamicResults();
            this._actionMode = false;
            this.load(config, appSite, timing);
        } finally {
            timing.endMeasure();
        }
    }

    // #endregion

    // #region Configuration Properties
    // NOTE: Config wrapper getters (defaultReplaceTemplateName, defaultSkipTemplateName,
    // defaultReplaceHtml, defaultSkipHtml, defaultSkipJson, maxArrayOperationItems, maxRecursionDepth)
    // are defined in loaderNormal_ILoader.ts and attached via Object.defineProperties
    declare readonly defaultReplaceTemplateName: string;
    declare readonly defaultSkipTemplateName: string;
    declare readonly defaultReplaceHtml: string;
    declare readonly defaultSkipHtml: string;
    declare readonly defaultSkipJson: string;
    declare readonly defaultAppViewPrefix: string;
    declare readonly maxArrayOperationItems: number;
    declare readonly maxRecursionDepth: number;
    declare readonly maxMergeIterations: number;

    /**
     * Gets the default app file name
     */
    get defaultAppFile(): string {
        return this._config!.defaultAppFile;
    }

    // skipDynamicView/skipDynamicData property accessors are
    // attached from loaderNormal_Accessors.ts (mirrors C# LoaderNormal_Accessors.cs
    // partial-class file). See Object.defineProperties wiring below.
    declare skipDynamicView: string | null;
    declare skipDynamicData: string | null;

    // #endregion

    // #region ILoaderNormal - Retrieval Related

    /**
     * Gets a template's HTML content and JSON with AppView and SearchAppSites fallback support.
     * Returns HTML, resolved JSON (with inheritance), and the resolved template name that was loaded.
     * This is the SINGLE method for all template retrieval - all lookup logic is consolidated here.
     *
     * Lookup order:
     * 1. Cache check
     * 2. Skip marker (!) check
     * 3. Direct lookup (fast path)
     * 4. Component reuse pattern check
     * 5. AppView fallback
     * 6. Primary template lookup (reuse pattern)
     * 7. SearchAppSites fallback
     * 8. Not found - return default
     * 9. Post-processing (inheritance, DynamicView)
     */
    getTemplate(appSite: string, currentTemplateName: string, appFileOrPlaceholderOrSlotholderName: string, timing: PhaseTiming, appView: string | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): GetTemplateResult {
        return timing.measure(TimingPhase.Bind, () => {
            this.validateCurrentTemplateName(appSite, currentTemplateName);
            appFileOrPlaceholderOrSlotholderName = this.resolveAppFileOrPlaceholderName(appSite, appView, appFileOrPlaceholderOrSlotholderName);

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`GetTemplate: appSite='${appSite}', currentTemplate='${currentTemplateName}', name='${appFileOrPlaceholderOrSlotholderName}', appView='${appView}', appViewPrefix='${this._config!.defaultAppViewPrefix}'`, 'LoaderNormal');
            }

            const appSiteTemplates = this.tryGetAppSiteTemplates(appSite);
            if (!appSiteTemplates) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`GetTemplate: AppSite '${appSite}' not found in cache, returning defaults`, 'LoaderNormal');
                }
                return { html: this._config!.defaultReplaceHtml, json: null, resolvedTemplateName: this._config!.defaultReplaceTemplateName, resolvedAppSite: appSite };
            }

            const skipResult = this.checkSkipMarkerAndReturn(appSite, appFileOrPlaceholderOrSlotholderName);
            if (skipResult) {
                return skipResult;
            }

            let html = '';
            let jsonObj: JsonObject | null = null;
            let templateKey = '';
            let resolvedAppSite = appSite;
            let found = false;
            const lookupContext: TemplateLookupContext = {
                requestAppSite: appSite,
                appSiteTemplates
            };

            ({ html, json: jsonObj, templateKey, resolvedAppSite, found } = this.tryResolveViaAppViewPriority(
                lookupContext, appView, appFileOrPlaceholderOrSlotholderName
            ));
            if (!found) {
                ({ html, json: jsonObj, templateKey, resolvedAppSite, found } = this.tryDirectTemplateLookup(
                    lookupContext, appFileOrPlaceholderOrSlotholderName
                ));
            }

            let baseTemplateName = appFileOrPlaceholderOrSlotholderName;
            let reuseJsonName: string | null = null;
            let isReusePattern = false;

            if (!found) {
                const parsed = CommonUtil.parseComponentReusePattern(appFileOrPlaceholderOrSlotholderName, currentTemplateName);
                baseTemplateName = parsed.baseTemplateName;
                reuseJsonName = parsed.reuseJsonName;
                isReusePattern = parsed.isReusePattern;
                ({ html, json: jsonObj, templateKey, resolvedAppSite, found } = this.tryComponentReuseLookup(
                    lookupContext, appFileOrPlaceholderOrSlotholderName, baseTemplateName, reuseJsonName, isReusePattern
                ));
            }

            if (!found) {
                ({ html, json: jsonObj, templateKey, resolvedAppSite, found } = this.tryAppViewFallback(
                    lookupContext, appView, baseTemplateName, reuseJsonName, appFileOrPlaceholderOrSlotholderName
                ));
            }

            if (!found && isReusePattern) {
                ({ html, json: jsonObj, templateKey, resolvedAppSite, found } = this.tryDirectTemplateLookup(
                    lookupContext, baseTemplateName, reuseJsonName, appFileOrPlaceholderOrSlotholderName
                ));
            }

            if (!found) {
                ({ html, json: jsonObj, templateKey, resolvedAppSite, found } = this.trySearchAppSitesLookup(
                    lookupContext, appView, baseTemplateName, reuseJsonName, appFileOrPlaceholderOrSlotholderName
                ));
            }

            // === 8. NOT FOUND - RETURN DEFAULT ===
            if (!found) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`GetTemplate: Template not found: '${appFileOrPlaceholderOrSlotholderName}' (base: '${baseTemplateName}'), returning defaults`, 'LoaderNormal');
                }
                html = this._config!.defaultReplaceHtml;
                jsonObj = null;
                templateKey = this._config!.defaultReplaceTemplateName;
                resolvedAppSite = appSite;
            }

            // === 9. POST-PROCESSING ===
            // Resolve JSON inheritance
            let json: JsonObject | null = null;
            if (jsonObj !== null && templateKey) {
                json = this.getTemplateDataWithInheritance(
                    appSite,
                    appView || '',
                    appFileOrPlaceholderOrSlotholderName,
                    jsonObj,
                    templateKey,
                    timing,
                    flowStack,
                    resolvedAppSite,
                    configJson,
                    html,
                    clientMode
                );
            }

            // NOTE: DynamicView handlers are applied by Engine via applyDynamicViewHandlers call (Area 7)

            return { html, json, resolvedTemplateName: templateKey, resolvedAppSite };
        });
    }

    getTemplateWithView(appSite: string, currentTemplateName: string, componentName: string, timing: PhaseTiming, appView: string | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): GetTemplateResult {
        return timing.measure(TimingPhase.Bind, () => {
            // clientMode threads into getTemplate → the inheritance seam, where applyClientHandlers consumes the
            // delivered island and enriches resolvedJson (the client mirror of the server data handler).
            const result = this.getTemplate(appSite, currentTemplateName, componentName, timing, appView, flowStack, configJson, clientMode);
            const html = this.applyDynamicViewHandlers(appSite, appView || '', currentTemplateName, componentName, flowStack?.length ?? 0, result.html, result.json, result.resolvedAppSite, result.resolvedTemplateName, configJson, timing);
            return { ...result, html };
        });
    }

    validateCurrentTemplateName(appSite: string, currentTemplateName: string): void {
        if (!currentTemplateName) {
            throw new Error("currentTemplateName must not be empty - it's required for component reuse pattern detection");
        }
    }

    resolveAppFileOrPlaceholderName(appSite: string, appView: string | null, appFileOrPlaceholderOrSlotholderName: string): string {
        if (appFileOrPlaceholderOrSlotholderName) {
            return appFileOrPlaceholderOrSlotholderName;
        }

        if (this._config && typeof this._config.getAppFile === 'function') {
            const result = this._config.getAppFile(appSite, appView || '');
            return result.appFile;
        }

        return this._config!.defaultAppFile;
    }

    tryGetAppSiteTemplates(appSite: string): Map<string, TemplateEntry> | null {
        const cached = this._store.tryGetTemplates(appSite);
        return cached ? cached : null;
    }

    checkSkipMarkerAndReturn(appSite: string, appFileOrPlaceholderOrSlotholderName: string): GetTemplateResult | null {
        if (!appFileOrPlaceholderOrSlotholderName.startsWith('!')) {
            return null;
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`GetTemplate: Skip marker detected in '${appFileOrPlaceholderOrSlotholderName}', returning skip defaults`, 'LoaderNormal');
        }
        return { html: this._config!.defaultSkipHtml, json: null, resolvedTemplateName: this._config!.defaultSkipTemplateName, resolvedAppSite: appSite };
    }

    tryResolveViaAppViewPriority(lookupContext: TemplateLookupContext, appView: string | null, appFileOrPlaceholderOrSlotholderName: string): TemplateLookupResult {
        const appSite = lookupContext.requestAppSite;
        if (!appView || !this._config!.defaultAppViewPrefix ||
            appView.toLowerCase() === this._config!.defaultAppViewPrefix.toLowerCase() ||
            !appFileOrPlaceholderOrSlotholderName.toLowerCase().includes(this._config!.defaultAppViewPrefix.toLowerCase())) {
            return { html: '', json: null, templateKey: '', resolvedAppSite: appSite, found: false };
        }

        const appViewName = CommonUtil.replaceCaseInsensitive(appFileOrPlaceholderOrSlotholderName, this._config!.defaultAppViewPrefix, appView);
        return this.tryDirectTemplateLookup(lookupContext, appViewName);
    }

    tryDirectTemplateLookup(lookupContext: TemplateLookupContext, templateName: string, reuseJsonName: string | null = null, originalName: string | null = null): TemplateLookupResult {
        const appSite = lookupContext.requestAppSite;
        const appSiteTemplates = lookupContext.appSiteTemplates;
        const directKey = `${appSite.toLowerCase()}_${templateName.toLowerCase()}`;
        if (appSiteTemplates.has(directKey)) {
            const directTemplate = appSiteTemplates.get(directKey)!;
            if (directTemplate.html) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`GetTemplate: Found direct match with HTML: '${directKey}'`, 'LoaderNormal');
                }
                return this.buildTemplateResolutionResult(lookupContext, templateName, directTemplate.html, directTemplate.json, appSite, reuseJsonName, originalName);
            }
        }

        return { html: '', json: null, templateKey: '', resolvedAppSite: appSite, found: false };
    }

    tryComponentReuseLookup(lookupContext: TemplateLookupContext, originalName: string, baseTemplateName: string, reuseJsonName: string | null, isReusePattern: boolean): TemplateLookupResult {
        const appSite = lookupContext.requestAppSite;
        if (!isReusePattern) {
            return { html: '', json: null, templateKey: '', resolvedAppSite: appSite, found: false };
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`GetTemplate: Component reuse detected - Original: '${originalName}', Base: '${baseTemplateName}', JSON: '${reuseJsonName}'`, 'LoaderNormal');
        }

        return this.tryDirectTemplateLookup(lookupContext, baseTemplateName, reuseJsonName, originalName);
    }

    tryAppViewFallback(lookupContext: TemplateLookupContext, appView: string | null, baseTemplateName: string, reuseJsonName: string | null, originalName: string): TemplateLookupResult {
        const appSite = lookupContext.requestAppSite;
        if (!appView || !this._config!.defaultAppViewPrefix ||
            !baseTemplateName.toLowerCase().includes(this._config!.defaultAppViewPrefix.toLowerCase())) {
            return { html: '', json: null, templateKey: '', resolvedAppSite: appSite, found: false };
        }

        const appViewTemplateName = CommonUtil.replaceCaseInsensitive(baseTemplateName, this._config!.defaultAppViewPrefix, appView);
        return this.tryDirectTemplateLookup(lookupContext, appViewTemplateName, reuseJsonName, originalName);
    }

    /**
     * Searches SearchAppSites for a matching template; within each search site the AppView-specific
     * name is tried before the base name.
     */
    trySearchAppSitesLookup(lookupContext: TemplateLookupContext, appView: string | null, baseTemplateName: string, reuseJsonName: string | null, originalName: string): TemplateLookupResult {
        const appSite = lookupContext.requestAppSite;
        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (!searchAppSites) {
            return { html: '', json: null, templateKey: '', resolvedAppSite: appSite, found: false };
        }

        const searchAppSitesArray = searchAppSites.split(',');
        for (let i = 0; i < searchAppSitesArray.length; i++) {
            const searchAppSite = searchAppSitesArray[i].trim();
            if (!searchAppSite) continue;

            const searchTemplates = this.tryGetAppSiteTemplates(searchAppSite);
            if (!searchTemplates) continue;
            const searchLookupContext: TemplateLookupContext = {
                requestAppSite: searchAppSite,
                appSiteTemplates: searchTemplates
            };

            const appViewResult = this.tryAppViewFallback(searchLookupContext, appView, baseTemplateName, reuseJsonName, originalName);
            if (appViewResult.found) {
                return appViewResult;
            }

            const searchKey = `${searchAppSite.toLowerCase()}_${baseTemplateName.toLowerCase()}`;
            if (searchTemplates.has(searchKey)) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`GetTemplate: Template '${baseTemplateName}' not found in '${appSite}', using fallback from '${searchAppSite}'`, 'LoaderNormal');
                }

                const searchTemplate = searchTemplates.get(searchKey)!;
                return this.buildTemplateResolutionResult(lookupContext, baseTemplateName, searchTemplate.html, searchTemplate.json, searchAppSite, reuseJsonName, originalName);
            }
        }

        return { html: '', json: null, templateKey: '', resolvedAppSite: appSite, found: false };
    }

    buildTemplateResolutionResult(lookupContext: TemplateLookupContext, resolvedTemplateName: string, html: string, defaultJson: JsonObject | null, resolvedAppSite: string, reuseJsonName: string | null = null, originalName: string | null = null): TemplateLookupResult {
        if (!originalName) {
            return { html, json: defaultJson, templateKey: resolvedTemplateName, resolvedAppSite, found: true };
        }

        const result = this.resolveReuseDataJson(lookupContext.appSiteTemplates, lookupContext.requestAppSite, reuseJsonName, defaultJson, resolvedTemplateName, originalName);
        return { html, json: result.json, templateKey: result.returnName, resolvedAppSite, found: true };
    }

    resolveReuseDataJson(
        appSiteTemplates: Map<string, TemplateEntry>,
        appSite: string,
        reuseJsonName: string | null,
        defaultJson: JsonObject | null,
        baseTemplateName: string,
        originalName: string
    ): ReuseDataJsonResult {
        if (!reuseJsonName) {
            return { json: defaultJson, returnName: baseTemplateName };
        }

        const reuseJsonKey = `${appSite.toLowerCase()}_${reuseJsonName.toLowerCase()}`;
        if (appSiteTemplates.has(reuseJsonKey)) {
            const reuseTemplate = appSiteTemplates.get(reuseJsonKey)!;
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`Using reuse JSON from: '${reuseJsonKey}'`, 'LoaderNormal');
            }
            return { json: reuseTemplate.json, returnName: originalName };
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`Reuse JSON not found: '${reuseJsonKey}', returning null (no fallback)`, 'LoaderNormal');
        }
        return { json: null, returnName: originalName };
    }

    // #endregion

    // #region ILoader Interface Implementation
    // NOTE: load(), getBaseComponentName(), getDynamicDataConfig(),
    // getAndClearInheritanceOperations() and other ILoader methods
    // are defined in loaderNormal_ILoader.ts and attached via Object.assign to prototype
    // #endregion
}

// Attach methods from split files (matching C# partial class pattern)
// Attach instance methods to prototype.
// accessorMethods uses Object.defineProperties via Object.getOwnPropertyDescriptors so
// property accessor pairs (get/set skipDynamicView etc.) are preserved on the prototype
// instead of being collapsed to their initial returned value (Object.assign quirk).
Object.defineProperties(LoaderNormal.prototype, Object.getOwnPropertyDescriptors(accessorMethods));
Object.assign(LoaderNormal.prototype, apiMethods);
Object.assign(LoaderNormal.prototype, lifecycleMethods);
Object.assign(LoaderNormal.prototype, iloaderMethods);
Object.assign(LoaderNormal.prototype, inheritanceMethods);
Object.assign(LoaderNormal.prototype, dynamicMethods);

// Attach config property descriptors (getters) from ILoader file
Object.defineProperties(LoaderNormal.prototype, configPropertyDescriptors);

// Attach static methods to class
Object.assign(LoaderNormal, accessorStaticMethods);
Object.assign(LoaderNormal, apiStaticMethods);
